/**
 * SideNav — Connection Source workspace sidebar.
 *
 *   • Always-visible 264px (expanded) / 76px (collapsed) rail on lg+ screens.
 *   • Pinned · Primary · Tools (categorized) · Games · Footer.
 *   • Active item: gradient pill, glowing left rail, fill-weight icon.
 *   • Ambient red→iris gradient blob + 1px shimmer line on the right edge.
 *   • Animated brand pulse, framer-motion section enter, CSS transitions
 *     for every hover/active state so 99% runs on the compositor.
 *
 * Hidden on touch / mobile breakpoints — the existing top header + bottom
 * tab bar continue to drive nav under lg.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretDown, CaretLeft, CaretRight, PushPin, Sparkle, Sun, Moon, SignOut, GearSix,
  MagnifyingGlass, Lightning,
} from '@phosphor-icons/react';
import ConnectionSourceLogo from './ConnectionSourceLogo';
import NotificationBell from '../notifications/NotificationBell';
import SyncStatusPill from './SyncStatusPill';

const COLLAPSE_KEY = 'cs.sideNavCollapsed';
const SECTIONS_KEY = 'cs.sideNavCollapsedSections';

const readBool = (k, def = false) => {
  try { const v = localStorage.getItem(k); return v == null ? def : v === '1'; } catch { return def; }
};
const writeBool = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* quota */ } };

const readSet = (k) => {
  try { const v = JSON.parse(localStorage.getItem(k) || '[]'); return new Set(Array.isArray(v) ? v : []); }
  catch { return new Set(); }
};
const writeSet = (k, s) => { try { localStorage.setItem(k, JSON.stringify(Array.from(s))); } catch { /* quota */ } };

/* ─────────────────────────────────────────────────────────────────────
   ITEM — single nav row, handles active/hover state + collapsed mode.
   ───────────────────────────────────────────────────────────────────── */
const Item = ({ to, icon: Icon, label, desc, color, external, collapsed, onClick, badge, pinned }) => {
  const accent = color || '#A3A3A3';

  const content = (isActive) => (
    <div className={`sidenav-item ${isActive ? 'is-active' : ''} ${collapsed ? 'is-collapsed' : ''}`}
      style={{
        '--accent': accent,
        '--accent-soft': `${accent}22`,
        '--accent-glow': `${accent}40`,
      }}>
      {/* Glowing left rail (only when active) */}
      <span aria-hidden className="sidenav-rail" />

      {/* Icon — Phosphor switches weight on active for a satisfying morph */}
      <span className="sidenav-icon">
        {Icon
          ? <Icon size={collapsed ? 19 : 17} weight={isActive ? 'fill' : 'regular'} />
          : <Lightning size={collapsed ? 19 : 17} />}
        {pinned && !collapsed && (
          <PushPin size={9} weight="fill" className="sidenav-pin-glyph" />
        )}
      </span>

      {/* Label */}
      {!collapsed && (
        <span className="sidenav-label">
          <span className="sidenav-label-text">{label}{external && <span className="sidenav-ext">↗</span>}</span>
          {desc && <span className="sidenav-label-desc">{desc}</span>}
        </span>
      )}

      {/* Trailing dot / badge */}
      {!collapsed && (isActive ? (
        <span aria-hidden className="sidenav-dot" />
      ) : badge ? (
        <span className="sidenav-badge">{badge}</span>
      ) : null)}

      {/* Tooltip when collapsed */}
      {collapsed && (
        <span className="sidenav-tooltip" role="tooltip">{label}</span>
      )}
    </div>
  );

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer"
        title={`${label} — opens in a new tab`}
        className="sidenav-link" onClick={onClick}
        data-testid={`sidenav-link-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {content(false)}
      </a>
    );
  }
  return (
    <NavLink to={to} end={to === '/me'} onClick={onClick}
      className={({ isActive }) => `sidenav-link ${isActive ? 'is-active' : ''}`}
      data-testid={`sidenav-link-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   SECTION — collapsible category with header + items.
   ───────────────────────────────────────────────────────────────────── */
const Section = ({ id, label, count, color = '#A3A3A3', collapsed, sectionOpen, onToggleSection, children }) => {
  if (collapsed) {
    // In collapsed mode, only render a thin divider between sections.
    return (
      <div className="sidenav-section is-collapsed">
        <div className="sidenav-section-divider" />
        <div className="sidenav-section-items">{children}</div>
      </div>
    );
  }
  return (
    <div className="sidenav-section" data-testid={`sidenav-section-${id}`}>
      <button
        type="button"
        onClick={() => onToggleSection(id)}
        className="sidenav-section-header"
        aria-expanded={sectionOpen}
        data-testid={`sidenav-section-toggle-${id}`}>
        <span className="sidenav-section-pip" style={{ background: color }} />
        <span className="sidenav-section-label">{label}</span>
        {typeof count === 'number' && <span className="sidenav-section-count">{count}</span>}
        <span className="sidenav-section-rule" />
        <CaretDown size={10} weight="bold"
          className={`sidenav-section-caret ${sectionOpen ? 'open' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {sectionOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="sidenav-section-body">
            <div className="sidenav-section-items">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────────────── */
const SideNav = ({
  navItems, toolsCategories, gamesItems, navColors, palette,
  user, paletteTools,
  pins, isPinned, togglePin,
  onOpenPalette, onOpenSettings, canAccessSettings,
  onLogout, onToggleTheme, isDark,
  onOpenNotifications,
}) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => readBool(COLLAPSE_KEY, false));
  const [closedSections, setClosedSections] = useState(() => readSet(SECTIONS_KEY));

  const persistCollapsed = useCallback((v) => { writeBool(COLLAPSE_KEY, v); setCollapsed(v); }, []);

  /* Publish the sidebar width on the root so `<main>` can pad-left around it.
     Only meaningful at lg+ — CSS gates the actual padding via a media query. */
  useEffect(() => {
    document.documentElement.style.setProperty('--sidenav-w-lg', `${collapsed ? 76 : 264}px`);
    return () => { document.documentElement.style.removeProperty('--sidenav-w-lg'); };
  }, [collapsed]);
  const toggleSection = useCallback((id) => {
    setClosedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      writeSet(SECTIONS_KEY, next);
      return next;
    });
  }, []);
  const isOpen = (id) => !closedSections.has(id);

  /* Resolve pinned items into full {icon, label, color, ...} records. */
  const byPath = useMemo(() => {
    const m = new Map();
    (paletteTools || []).forEach(t => m.set(t.path, t));
    return m;
  }, [paletteTools]);
  const pinnedResolved = useMemo(
    () => (pins || []).map(p => byPath.get(p)).filter(Boolean),
    [pins, byPath],
  );

  /* Keyboard: collapse / expand with [   (no modifier) when not in input. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '[') return;
      const el = document.activeElement;
      const tag = el?.tagName;
      const editable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      persistCollapsed(!collapsed);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed, persistCollapsed]);

  return (
    <>
      <aside className={`sidenav ${collapsed ? 'is-collapsed' : ''}`}
        style={{ width: collapsed ? 76 : 264 }}
        data-testid="sidenav"
        aria-label="Primary navigation">

        {/* ── Ambient layers (decorative, pointer-events: none) ── */}
        <div aria-hidden className="sidenav-ambient">
          <div className="sidenav-blob sidenav-blob-top" />
          <div className="sidenav-blob sidenav-blob-bottom" />
          <div className="sidenav-shimmer-line" />
          <div className="sidenav-grain" />
        </div>

        {/* ── Brand ── */}
        <div className={`sidenav-brand ${collapsed ? 'is-collapsed' : ''}`}>
          <NavLink to="/" className="sidenav-brand-link" data-testid="sidenav-brand">
            <span className="sidenav-brand-mark">
              <ConnectionSourceLogo size="sm" compact />
              <span aria-hidden className="sidenav-brand-pulse" />
            </span>
            {!collapsed && (
              <span className="sidenav-brand-text">
                <span className="sidenav-brand-line-1">Connection</span>
                <span className="sidenav-brand-line-2">Source</span>
              </span>
            )}
          </NavLink>
        </div>

        {/* ── Quick search trigger ── */}
        <button type="button" onClick={onOpenPalette}
          className={`sidenav-search ${collapsed ? 'is-collapsed' : ''}`}
          title="Quick search (⌘K)"
          data-testid="sidenav-search">
          <MagnifyingGlass size={collapsed ? 16 : 13} weight="bold" />
          {!collapsed && (
            <>
              <span className="sidenav-search-text">Quick search</span>
              <kbd className="sidenav-kbd">⌘K</kbd>
            </>
          )}
        </button>

        {/* ── Scrollable nav body ── */}
        <div className="sidenav-body cs-hide-scrollbar">
          {/* PINNED */}
          {pinnedResolved.length > 0 && (
            <Section id="pinned" label="Pinned" count={pinnedResolved.length}
              color="#F59E0B"
              collapsed={collapsed} sectionOpen={isOpen('pinned')} onToggleSection={toggleSection}>
              {pinnedResolved.map(it => (
                <Item key={`pin-${it.path}`} to={it.path}
                  icon={it.icon} label={it.label} desc={it.desc}
                  color={it.color || '#A3A3A3'}
                  external={it.external}
                  collapsed={collapsed}
                  pinned />
              ))}
            </Section>
          )}

          {/* PRIMARY */}
          <Section id="primary" label="Navigation" count={navItems.length}
            color={palette.red}
            collapsed={collapsed} sectionOpen={isOpen('primary')} onToggleSection={toggleSection}>
            {navItems.map(n => (
              <Item key={n.path} to={n.path}
                icon={n.icon} label={n.label}
                color={navColors[n.label] || palette.red}
                collapsed={collapsed} />
            ))}
          </Section>

          {/* TOOL CATEGORIES */}
          {toolsCategories.map(cat => (
            <Section
              key={cat.label}
              id={`cat-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}
              label={cat.label}
              count={cat.items.length}
              color={cat.color}
              collapsed={collapsed}
              sectionOpen={isOpen(`cat-${cat.label.toLowerCase().replace(/\s+/g, '-')}`)}
              onToggleSection={toggleSection}>
              {cat.items.map(it => (
                <Item key={it.path} to={it.path}
                  icon={it.icon} label={it.label} desc={it.desc}
                  color={navColors[it.label] || cat.color}
                  external={it.external}
                  collapsed={collapsed}
                  pinned={isPinned(it.path)} />
              ))}
            </Section>
          ))}

          {/* GAMES */}
          {gamesItems?.length > 0 && (
            <Section id="games" label="Games" count={gamesItems.length}
              color={palette.iris}
              collapsed={collapsed} sectionOpen={isOpen('games')} onToggleSection={toggleSection}>
              {gamesItems.map(g => (
                <Item key={g.path} to={g.path}
                  icon={g.icon} label={g.label} desc={g.desc}
                  color={navColors[g.label] || palette.iris}
                  collapsed={collapsed} />
              ))}
            </Section>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={`sidenav-footer ${collapsed ? 'is-collapsed' : ''}`}>
          {/* Avatar + role chip */}
          {!collapsed && (
            <div className="sidenav-user">
              <span className="sidenav-avatar">
                {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
                <span aria-hidden className="sidenav-avatar-ring" />
              </span>
              <span className="sidenav-user-text">
                <span className="sidenav-user-name">{user?.name || user?.email?.split('@')[0] || 'You'}</span>
                <span className="sidenav-user-role">{user?.role === 'admin' || user?.is_manager ? 'Manager' : 'Rep'}</span>
              </span>
            </div>
          )}

          {/* Footer action row */}
          <div className={`sidenav-actions ${collapsed ? 'is-collapsed' : ''}`}>
            <div className="sidenav-action sidenav-action-notif" title="Notifications">
              <NotificationBell onOpen={onOpenNotifications} />
            </div>
            <SyncStatusPill />
            <button type="button" onClick={onToggleTheme}
              className="sidenav-action" title={isDark ? 'Light mode' : 'Dark mode'}
              data-testid="sidenav-theme">
              {isDark ? <Sun size={15} weight="bold" /> : <Moon size={15} weight="bold" />}
            </button>
            {canAccessSettings && (
              <button type="button" onClick={onOpenSettings}
                className="sidenav-action" title="Settings"
                data-testid="sidenav-settings">
                <GearSix size={15} weight="bold" />
              </button>
            )}
            <button type="button" onClick={onLogout}
              className="sidenav-action sidenav-action-danger" title="Sign out"
              data-testid="sidenav-logout">
              <SignOut size={15} weight="bold" />
            </button>
          </div>

          {/* Collapse toggle */}
          <button type="button" onClick={() => persistCollapsed(!collapsed)}
            className="sidenav-collapse-btn" title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidenav-collapse-toggle">
            {collapsed ? <CaretRight size={12} weight="bold" /> : <CaretLeft size={12} weight="bold" />}
            {!collapsed && <span>Collapse</span>}
            {!collapsed && <kbd className="sidenav-kbd sidenav-kbd-sm">[</kbd>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SideNav;
